/**
 * 通用打印工具
 * 对齐商业版：每个列表页提供「打印」，弹出打印页（含单位/期间/页眉页脚/签署栏），可直接打印或另存 PDF。
 *
 * 用法一（直接打印页面上的表格）：
 *   import { printTable } from 'js/common/print'
 *   printTable({ title: '明细账', company: 'XX公司', period: '2026年01期', elementId: 'printTable' })
 *
 * 用法二（用数据渲染）：
 *   printTable({ title: '科目余额表', head: ['科目编码','科目名称','期初余额'], rows: [[...]], align: ['left','left','right'] })
 */
function esc(s) {
	return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function defaultStyle(opt) {
	const fs = opt.fontSize || 12;
	const gap = opt.cellGap == null ? 0 : opt.cellGap;
	return `
		@page { size: ${opt.paper || 'A4'} ${opt.landscape ? 'landscape' : 'portrait'};
			margin: ${opt.margin ? opt.margin.top : 10}mm ${opt.margin ? opt.margin.right : 10}mm ${opt.margin ? opt.margin.bottom : 10}mm ${opt.margin ? opt.margin.left : 10}mm; }
		* { box-sizing: border-box; }
		body { margin: 0; font-family: "Microsoft YaHei", SimSun, Arial, sans-serif; font-size: ${fs}px; color: #000; }
		.p-title { text-align: center; font-size: ${fs + 8}px; font-weight: bold; letter-spacing: 2px; margin: 6px 0 2px; }
		.p-sub { text-align: center; font-size: ${fs}px; color: #333; margin-bottom: 6px; }
		.p-meta { display: flex; justify-content: space-between; font-size: ${fs - 1}px; color: #333; margin-bottom: 4px; }
		table { width: 100%; border-collapse: collapse; }
		table td, table th { border: 1px solid #333; padding: ${2 + gap}px 6px; line-height: 1.5; }
		table th, table thead td { background: #f0f0f0 !important; font-weight: bold; text-align: center; }
		.t-left { text-align: left; } .t-right { text-align: right; } .t-center { text-align: center; }
		.p-sign { display: flex; justify-content: space-between; margin-top: 14px; font-size: ${fs}px; }
		.p-sign span { flex: 1; }
		.p-foot { margin-top: 10px; font-size: ${fs - 1}px; color: #555; display: flex; justify-content: space-between; }
		${opt.styleExtra || ''}
	`;
}

/**
 * 生成打印页完整 HTML
 */
export function buildPrintHtml(opt) {
	const head = opt.head || [];
	const align = opt.align || [];
	let tableHtml = '';

	if (opt.elementId) {
		const el = document.getElementById(opt.elementId);
		tableHtml = el ? el.outerHTML : '<p>（无可打印内容）</p>';
	} else {
		const ths = head.map((h, i) => `<th class="${align[i] === 'right' ? 't-right' : align[i] === 'left' ? 't-left' : 't-center'}">${esc(h)}</th>`).join('');
		const trs = (opt.rows || []).map(r => {
			const tds = (r || []).map((c, i) => {
				if (c && typeof c === 'object' && c.html != null) return `<td class="${c.cls || ''}" ${c.colspan ? 'colspan="' + c.colspan + '"' : ''}>${c.html}</td>`;
				const cls = align[i] === 'right' ? 't-right' : align[i] === 'left' ? 't-left' : 't-center';
				return `<td class="${cls}">${esc(c)}</td>`;
			}).join('');
			return `<tr>${tds}</tr>`;
		}).join('');
		tableHtml = `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody>${opt.footRow ? '<tfoot>' + opt.footRow + '</tfoot>' : ''}</table>`;
	}

	const sign = opt.sign === false ? '' : (opt.sign || []).map(s => `<span>${esc(s.name || s)}：${esc(s.value || '')}</span>`).join('');
	const now = new Date();
	const pad = n => n < 10 ? '0' + n : '' + n;
	const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

	return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(opt.title || '打印')}</title>
<style>${defaultStyle(opt)}</style></head><body>
<div class="p-title">${esc(opt.title || '')}</div>
${opt.subTitle ? `<div class="p-sub">${esc(opt.subTitle)}</div>` : ''}
<div class="p-meta"><span>${esc(opt.company || '')}</span><span>${esc(opt.period || '')}</span></div>
${opt.extraTop || ''}
${tableHtml}
${sign ? `<div class="p-sign">${sign}</div>` : ''}
<div class="p-foot"><span>打印日期：${dateStr}</span><span>${esc(opt.footNote || '')}</span></div>
<script>window.onload=function(){ setTimeout(function(){ window.print(); }, 300); };<\/script>
</body></html>`;
}

/**
 * 打开打印窗口
 */
export function printTable(opt) {
	const w = window.open('', '_blank');
	if (!w) {
		alert('打印窗口被浏览器拦截，请允许弹出窗口后重试');
		return;
	}
	w.document.open();
	w.document.write(buildPrintHtml(opt));
	w.document.close();
}

/**
 * 打印页面上已存在的元素（最省事，表格样式由页面决定）
 */
export function printElement(elementId, opt) {
	return printTable(Object.assign({ elementId: elementId }, opt || {}));
}
